/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import type { Props } from 'bundles/page-config-common/components/ConsumerComponentsConfigRenderer';
import GroupedComponentsWrapperRenderer from 'bundles/page-config-common/module-renderers/GroupedComponentsWrapperRenderer';
import SkillFocusModuleRenderer from 'bundles/page-config-common/module-renderers/SkillFocusModuleRenderer';
import EmbeddedPromoTemplatePageModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EmbeddedPromoTemplatePageModuleRenderer';
import EnterpriseAssignedCoursesModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseAssignedCoursesModuleRenderer';
import EnterpriseBrowseCollectionModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseBrowseCollectionModuleRenderer';
import EnterpriseContinueLearningModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseContinueLearningModuleRenderer';
import EnterpriseCuratedCollectionAndBadgeModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseCuratedCollectionAndBadgeModuleRenderer';
import EnterpriseDomainPillsModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseDomainPillsModuleRenderer';
import EnterpriseForCreditModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseForCreditModuleRenderer';
import EnterpriseLevelSetsCardsModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseLevelSetsCardsModuleRenderer';
import EnterpriseOnboardingResultsHeaderModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseOnboardingResultsHeaderModuleRenderer';
import EnterpriseSkillClipsModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseSkillClipsModuleRenderer';
import EnterpriseSkillSetCardsModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseSkillSetCardsModuleRenderer';
import GoogleProgramBannerModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/GoogleProgramBannerModuleRenderer';
import GoogleProgramFooterModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/GoogleProgramFooterModuleRenderer';
import SocialShareCardModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/SocialShareCardModuleRenderer';
import type { PageConfig_GroupedComponentsWrapperMetadataFragment as GroupedComponentsWrapper } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';

// Default components config renderer for an enterprise context page.
// Create a new config renderer component if your page needs to interpret components differently.
const EnterpriseComponentsConfigRenderer = ({ pageConfigComponents }: Props) => {
  if (!pageConfigComponents || !pageConfigComponents.length) {
    return null;
  }

  return (
    <React.Fragment>
      {pageConfigComponents.map((component) => {
        switch (component?.__typename) {
          case 'PageConfig_GroupedComponentsWrapper':
            return (
              <GroupedComponentsWrapperRenderer
                key={component.id}
                pageConfigComponent={component as GroupedComponentsWrapper}
                ConfigRenderer={EnterpriseComponentsConfigRenderer}
              />
            );
          case 'PageConfig_OnboardingResultsHeaderComponent':
            return <EnterpriseOnboardingResultsHeaderModuleRenderer key={component.id} />;
          case 'PageConfig_EnrolledProductsComponent':
            return <EnterpriseContinueLearningModuleRenderer key={component.id} pageConfigComponent={component} />;
          case 'PageConfig_DomainPillsComponent':
            return <EnterpriseDomainPillsModuleRenderer key={component.id} />;
          case 'PageConfig_EmbeddedPromoTemplatePage':
            return <EmbeddedPromoTemplatePageModuleRenderer key={component.id} />;
          case 'PageConfig_EnterpriseForCreditCollectionComponent':
            return <EnterpriseForCreditModuleRenderer key={component.id} />;
          case 'PageConfig_EnterpriseBrowseCollectionComponent':
            return <EnterpriseBrowseCollectionModuleRenderer key={component.id} />;
          case 'PageConfig_EnterpriseAssignedCourses':
            return <EnterpriseAssignedCoursesModuleRenderer key={component.id} />;
          case 'PageConfig_EnterpriseSocialShareCard':
            return <SocialShareCardModuleRenderer key={component.id} />;
          case 'PageConfig_GoogleProgramBanner':
            return <GoogleProgramBannerModuleRenderer key={component.id} />;
          case 'PageConfig_GoogleProgramFooter':
            return <GoogleProgramFooterModuleRenderer key={component.id} />;
          case 'PageConfig_EnterpriseSkillClipsCollectionComponent':
            return <EnterpriseSkillClipsModuleRenderer key={component.id} />;
          case 'PageConfig_EnterpriseCuratedCollectionAndBadgeComponent':
            return <EnterpriseCuratedCollectionAndBadgeModuleRenderer key={component.id} />;
          case 'PageConfig_SkillSetCardsComponent':
            return <EnterpriseSkillSetCardsModuleRenderer key={component.id} />;
          case 'PageConfig_SkillsFocusComponent':
            return <SkillFocusModuleRenderer key={component.id} />;
          case 'PageConfig_LevelSets':
            return <EnterpriseLevelSetsCardsModuleRenderer key={component.id} />;
          // @ts-expect-error Testing purposes only
          case 'PageConfig_SandboxTestComponent':
            return <div key="PageConfig_SandboxTestComponent">SANDBOX_TEST_COMPONENT</div>;
          default:
            return null;
        }
      })}
    </React.Fragment>
  );
};

export default EnterpriseComponentsConfigRenderer;
